import React, { useState } from "react";
import {
  useGetComputeCapacityListQuery,
  useDeleteComputeCapacityMutation,
} from "../store/api/computeCapacityApi";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Input } from "../components/ui/input";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../components/ui/pagination";
import { Search, Pencil, Trash2, Plus } from "lucide-react";
import { Button } from "../components/ui/button";
import ComputeCapacityFormModal from "../components/modals/ComputeCapacityFormModal";
import ConfirmModal from "../components/modals/ConfirmModal";
import SearchInput from "../components/SearchInput";

const ComputeCapacity = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [formMode, setFormMode] = useState("create");
  const limit = 10;

  const { data, isLoading, error } = useGetComputeCapacityListQuery({
    limit,
    offset: (page - 1) * limit,
    search,
  });

  const [deleteCapacity, { isLoading: isDeleting }] =
    useDeleteComputeCapacityMutation();

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleCreate = () => {
    setSelectedId(null);
    setFormMode("create");
    setFormModalOpen(true);
  };

  const handleEdit = (id) => {
    setSelectedId(id);
    setFormMode("edit");
    setFormModalOpen(true);
  };

  const handleDelete = (id) => {
    setSelectedId(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteCapacity(selectedId).unwrap();
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error("Failed to delete compute capacity:", error);
    }
  };

  const totalPages = data ? Math.ceil(data.pagination.total / limit) : 0;

  return (
    <>
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <h1 className="text-md font-bold">Compute Capacity</h1>
            <Button
              onClick={handleCreate}
              className="flex items-center space-x-2"
            >
              <Plus className="h-4 w-4" />
              <span>Add New</span>
            </Button>
          </div>
          <div className="">
            <SearchInput search={search} handleSearch={handleSearch} />
          </div>
        </div>

        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Compute ID</TableHead>
                <TableHead>Total Unit</TableHead>
                <TableHead>Available Units</TableHead>
                <TableHead>Allocated Units</TableHead>
                <TableHead>Deployed Units</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className="text-center text-destructive"
                  >
                    Error loading data
                  </TableCell>
                </TableRow>
              ) : data?.data?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center">
                    No data found
                  </TableCell>
                </TableRow>
              ) : (
                data?.data?.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium">
                      {item.computeId}
                    </TableCell>
                    <TableCell>{item.totalUnit}</TableCell>
                    <TableCell>{item.availableUnit}</TableCell>
                    <TableCell>{item.allocatedUnit}</TableCell>
                    <TableCell>{item.deployedUnit}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-4 w-4"
                          onClick={() => handleEdit(item.id)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-4 w-4 text-red-500 hover:text-red-500"
                          onClick={() => handleDelete(item.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {totalPages > 0 && (
          <Pagination className="justify-center">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                />
              </PaginationItem>
              {[...Array(totalPages)].map((_, i) => (
                <PaginationItem key={i + 1}>
                  <PaginationLink
                    onClick={() => setPage(i + 1)}
                    isActive={page === i + 1}
                  >
                    {i + 1}
                  </PaginationLink>
                </PaginationItem>
              ))}
              <PaginationItem>
                <PaginationNext
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>

      <ComputeCapacityFormModal
        isOpen={formModalOpen}
        onClose={() => setFormModalOpen(false)}
        id={selectedId}
        mode={formMode}
      />

      {/* Delete Confirm Modal */}
      <ConfirmModal
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Delete User"
        isDeleting={isDeleting}
        description="Are you sure you want to delete this Compute Capacity?"
      />
    </>
  );
};

export default ComputeCapacity;
